import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import "./mascot.css";

export type Mood = "idle" | "listening" | "thinking" | "happy" | "sleeping";

/** 软糖身体：圆顶、下面稍宽的圆角方块（和 iOS 的 MascotBody 同一组比例，宽 100 高 90） */
const BODY = "M50 0C80 0 100 14.4 100 46.8C100 86.4 88 90 50 90C12 90 0 86.4 0 46.8C0 14.4 20 0 50 0Z";

interface Props {
  mood?: Mood;
  size?: number;
  /** 点一下跳一下、按住眼睛跟手（小尺寸的状态图标关掉） */
  interactive?: boolean;
  /** 静止版（常驻在界面上的小头像用，不跑动画） */
  animated?: boolean;
  /** 反色：深色底上的白色 Timi */
  inverted?: boolean;
}

/**
 * Timi：App 的形象。一颗会呼吸的软糖 + 两只会眨的眼睛，情绪全靠眼睛和身体的动作（对应 iOS 的 TimiMascot）。
 * - 发呆：呼吸、随机眨眼、偶尔左右瞟一眼歪头
 * - 在听：你在打字时，眼睛往下看向输入框
 * - 在想：眼睛往右上转来转去，身体轻轻摇
 * - 开心：眯眼 ^ ^，跳一下（点它也会）
 * - 睡着：没连上服务器时，眼睛闭成两条线
 */
export function TimiMascot({ mood = "idle", size = 96, interactive = true, animated = true, inverted = false }: Props) {
  const [blink, setBlink] = useState(false);
  const [look, setLook] = useState<[number, number]>([0, 0]);
  const [tilt, setTilt] = useState(0);
  const [touchLook, setTouchLook] = useState<[number, number] | null>(null);
  const [cheer, setCheer] = useState(false);
  const [jump, setJump] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const reduce = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const shown: Mood = cheer ? "happy" : mood;

  // 眨眼、东张西望、思考时转眼珠；mood 变了重新开始
  useEffect(() => {
    if (!animated || reduce) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const wait = (ms: number) => new Promise<void>((r) => timers.push(setTimeout(r, ms)));
    const doBlink = async () => { setBlink(true); await wait(110); setBlink(false); };
    (async () => {
      if (mood === "happy") { setJump(true); await wait(180); setJump(false); }
      setLook(mood === "thinking" ? [0.05, -0.05] : [0, 0]);
      while (!cancelled) {
        if (mood === "thinking") {
          for (const target of [[0.06, -0.05], [-0.02, -0.06], [0.03, -0.02]] as [number, number][]) {
            setLook(target);
            await wait(650);
            if (cancelled) return;
          }
        } else if (mood === "idle" || mood === "listening") {
          await wait(2200 + Math.random() * 2600);
          if (cancelled) return;
          await doBlink();
          if (Math.random() < 0.25) { await wait(140); await doBlink(); }
          if (mood === "idle" && Math.random() < 0.35) {
            const side = Math.random() < 0.5 ? 0.06 : -0.06;
            setLook([side, 0]);
            setTilt(side > 0 ? 6 : -6);
            await wait(900 + Math.random() * 700);
            setLook([0, 0]);
            setTilt(0);
          }
        } else {
          return;
        }
      }
    })();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [mood, animated, reduce]);

  const current: [number, number] = touchLook
    ?? (shown === "listening" ? [-0.02, 0.05] : shown === "sleeping" ? [0, 0.03] : look);

  const hop = () => {
    setJump(true);
    setCheer(true);
    setTimeout(() => setJump(false), 170);
    setTimeout(() => setCheer(false), 870);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!interactive || !(e.buttons & 1) || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const dx = (e.clientX - r.left - r.width / 2) / r.width;
    const dy = (e.clientY - r.top - r.height / 2) / r.height;
    setTouchLook([Math.max(-0.09, Math.min(0.09, dx * 0.2)), Math.max(-0.07, Math.min(0.07, dy * 0.2))]);
  };

  const bodyFill = inverted ? "var(--bg)" : "var(--text)";
  const eyeFill = inverted ? "var(--text)" : "var(--bg)";
  // 眼睛中心：身体中心往上 0.1，左右各 0.11（以宽度为 1）
  const ex = current[0] * 100;
  const ey = current[1] * 100;
  const grow = shown === "listening" ? 1.08 : 1;

  const style: CSSProperties = { width: size, height: size, ["--tilt" as string]: `${tilt}deg` };
  const cls = [
    "timi",
    animated && !reduce ? "timi-live" : "",
    mood === "sleeping" ? "timi-sleepy" : "",
    shown === "thinking" && !reduce ? "timi-sway" : "",
    jump ? "timi-jump" : "",
    interactive ? "timi-interactive" : "",
  ].join(" ");

  return (
    <div
      ref={ref}
      className={cls}
      style={style}
      aria-hidden="true"
      onClick={interactive ? hop : undefined}
      onPointerMove={onPointerMove}
      onPointerUp={() => setTouchLook(null)}
      onPointerLeave={() => setTouchLook(null)}
    >
      <svg viewBox="0 -5 100 100" width={size} height={size}>
        <g className="timi-body">
          <path d={BODY} fill={bodyFill} />
          <g style={{ transform: `translate(${ex}px, ${ey}px)`, transition: touchLook ? "none" : "transform 0.35s cubic-bezier(.3,.7,.3,1)" }}>
            {[39, 61].map((cx) => {
              if (shown === "happy") {
                return <path key={cx} d={`M${cx - 7.5} 39 Q${cx} 27.8 ${cx + 7.5} 39`} fill="none" stroke={eyeFill} strokeWidth={6} strokeLinecap="round" />;
              }
              if (shown === "sleeping") {
                return <rect key={cx} x={cx - 7.5} y={35.75} width={15} height={4.5} rx={2.25} fill={eyeFill} />;
              }
              const w = 11 * grow;
              const h = 22 * grow * (blink ? 0.12 : 1);
              return (
                <rect key={cx} x={cx - w / 2} y={35 - h / 2} width={w} height={h} rx={w / 2} fill={eyeFill}
                  style={{ transition: "y 0.08s, height 0.08s" }} />
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
}

/** 圆形头像版：默认机器人没有自定义头像时用它 */
export function TimiBadge({ size = 28 }: { size?: number }) {
  return (
    <div className="timi-badge" style={{ width: size, height: size }}>
      <div style={{ transform: `translateY(${size * 0.04}px)` }}>
        <TimiMascot size={size * 0.6} interactive={false} animated={false} inverted />
      </div>
    </div>
  );
}
